import { useEffect, useRef, useState } from "react";
import _ from "lodash";
import Quill from "quill";
import createDOMPurify from "dompurify";

import { FormatType, TextElementProps, TextType } from "./types";

const TextElement = ({
  value,
  format,
  type = TextType.REGULAR,
  multiple = false,
}: TextElementProps) => {
  const quillRefs = useRef<{ [key: string]: Quill | null }>({});
  const DOMPurify = createDOMPurify(window);
  const [elementIds, setElementIds] = useState<string[]>([]);

  useEffect(() => {
    if (type !== TextType.RICH_TEXT) return;

    const values = Array.isArray(value) && multiple ? value : [value];
    const ids = values.map(() => `quill-read-${_.uniqueId()}`);
    setElementIds(ids);

    return () => {
      quillRefs.current = {};
    };
  }, [value, multiple, type]);

  useEffect(() => {
    if (type !== TextType.RICH_TEXT || elementIds.length === 0) return;

    const values = Array.isArray(value) ? value : [value];

    elementIds.forEach((id, index) => {
      const val = values[index];
      if (!val) return;

      setTimeout(() => {
        if (!quillRefs.current[id]) {
          const element = document.getElementById(id);
          if (!element) return;

          const quill = new Quill(`#${id}`, {
            readOnly: true,
            modules: { toolbar: null },
          });

          quill.clipboard.dangerouslyPasteHTML(
            DOMPurify.sanitize(val, { USE_PROFILES: { html: true } }),
          );

          quillRefs.current[id] = quill;
        }
      }, 0);
    });
  }, [elementIds, value, multiple, type]);

  const renderValue = (val: string | undefined | null) => {
    if (!val) return null;

    if (format && [FormatType.LINK, FormatType.IMAGE].includes(format)) {
      return (
        <a href={val} target="_blank" rel="noopener noreferrer">
          {val}
        </a>
      );
    }
    return <span>{val}</span>;
  };

  if (type === TextType.RICH_TEXT) {
    if (multiple) {
      const valueArray = Array.isArray(value) ? value : value ? [value] : [];

      return (
        <ul className="list-disc pl-5">
          {valueArray.map((_, index) => (
            <li key={index} className="mb-1">
              {index < elementIds.length ? (
                <div id={elementIds[index]} className="quill-read-only"></div>
              ) : null}
            </li>
          ))}
        </ul>
      );
    }

    return elementIds.length > 0 ? (
      <div id={elementIds[0]} className="quill-read-only"></div>
    ) : null;
  }

  if (multiple) {
    const valueArray = Array.isArray(value) ? value : value ? [value] : [];

    if (valueArray.length > 0) {
      return (
        <ul className="list-disc pl-5">
          {valueArray.map((item, index) => (
            <li key={index} className="mb-1">
              {renderValue(item)}
            </li>
          ))}
        </ul>
      );
    }

    return <ul className="list-disc pl-5"></ul>;
  }

  const singleValue = Array.isArray(value) ? value[0] : value;
  return renderValue(singleValue);
};

export default TextElement;
